import { FormControl, InputLabel, MenuItem, Select, SelectChangeEvent, Switch } from '@mui/material';
import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import Modal from 'react-modal';
import { iclApi } from 'api';
import { getStudents, getStudentsGroup, subjectsObj } from 'Common/Database';
import { useAuthContext } from 'shared/hooks';
import { Exam, HW, ICL, WorkType } from 'shared/types';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  currentICL: ICL | HW | Exam | null;
}

export const SendToStudentsModal = ({ isOpen, onClose, currentICL }: Props) => {
  const {
    t,
    i18n: { language: platformLang },
  } = useTranslation();
  const { user } = useAuthContext();

  const [isGroup, setIsGroup] = useState(false);
  const [isCorrection, setIsCorrection] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [selectedStudentName, setSelectedStudentName] = useState('');
  const [selectedGroupName, setSelectedGroupName] = useState('');
  const [students, setStudents] = useState([]);

  const [studentsGroups, setStudentsGroups] = useState([]);

  const option = platformLang === 'ua' ? currentICL?.subject?.name : (currentICL?.subject?.ruName as string);

  const admins = user?.type === 'NewAdmin' || user?.type === 'Admin' || user?.type === 'Superadmin';

  const fetchStudentsGroup = async () => {
    await getStudentsGroup().then(data => {
      if (data) {
        const arrGroup = Object.values(data);

        const filteredGroups = arrGroup?.filter(group => {
          const subjects = group.subjects;
          if (!admins) {
            if (group.subjects) {
              for (const subjectName in subjects) {
                const subject = subjects[subjectName];
                if (subject.attachedTeacher && subject.attachedTeacher.id === user.mail) {
                  return true;
                }
              }
            }
          } else {
            if (group.subjects) {
              for (const subjectName in subjects) {
                const subject = subjects[subjectName];
                if (subject.activeSub) {
                  return true;
                }
              }
            }
          }
        });

        setStudentsGroups(filteredGroups);
      }
    });
  };

  useEffect(() => {
    const init = async () => {
      setIsLoading(true);

      try {
        await fetchStudentsGroup();

        const res = getStudents();
        res.then(resp => {
          const arrStudents = Object.values(resp);
          setStudents(arrStudents);
        });
      } catch (error) {
        console.log(error);
      }

      setIsLoading(false);
    };

    init();
  }, []);

  const handleIsChecked = (event: React.ChangeEvent<HTMLInputElement>) => setIsGroup(event.target.checked);
  const changeStudent = (event: SelectChangeEvent<string>) => setSelectedStudentName(event.target.value);
  const changeStudentGroup = (event: SelectChangeEvent<string>) => setSelectedGroupName(event.target.value);

  const checkIfSubmitButtonDisabled = () => {
    if (isSending || !currentICL) {
      return true;
    }

    if (isGroup) {
      if (selectedGroupName) {
        return false;
      }
    }

    if (!isGroup) {
      if (selectedStudentName) {
        return false;
      }
    }

    return true;
  };

  const resetFields = () => {
    setSelectedStudentName('');
    setSelectedGroupName('');
  };

  const closeModal = () => {
    resetFields();
    onClose();
  };

  const sendICLToStudents = async () => {
    if (!currentICL) {
      return;
    }

    const studentEmail = students?.find(student => student?.FIO === selectedStudentName)?.email;
    const studentsEmails = studentsGroups
      ?.find(group => group?.groupName === selectedGroupName)
      ?.students?.map(student => student?.email);
    const emails: string[] = isGroup ? studentsEmails : [studentEmail];
    const emailsWithoutDots = emails.map(email => email.replace(/[.]/g, '')); // it's required because there are no dots in user.email that signed in site

    setIsSending(true);

    try {
      await iclApi.sendICLToStudents(
        { ...currentICL, ...(isCorrection ? { correction: true } : {}) },
        emailsWithoutDots,
      );
      toast.success(t('Successfully sent!'));
      closeModal();
    } catch (error) {
      console.log(error);
      toast.error(t('Something went wrong. Try again!'));
    }

    setIsSending(false);
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={closeModal}
      className="bottom-auto mx-auto w-[85%] translate-y-[25vh] rounded-xl bg-[#FAF8F4] p-[30px] lg:w-[520px]"
      overlayClassName="z-10 w-full fixed top-0 left-0 rigth-0 bottom-0 bg-[#0d0d0ccc]"
      closeTimeoutMS={300}
    >
      <div className="popup-send-homework-content">
        <h3 className="text-center text-[20px] lg:text-[32px]">{isGroup ? t('send group') : t('send student')}</h3>

        <p className="text-center text-[16px] lg:text-[20px]">
          {isGroup ? t('description of send to group') : t('description of send to student')}{' '}
          {currentICL?.type === WorkType.hw
            ? ` ${t('Homework hm')}`
            : currentICL?.type === WorkType.exam
              ? ` ${t('exam name')}`
              : `${t('checkLists')}`}
        </p>

        <div className="m-auto w-fit pb-[20px]">
          <Switch checked={isGroup} onChange={handleIsChecked} inputProps={{ 'aria-label': 'controlled' }} />
          <span className="title">{isGroup ? t('Group of students') : t('To one student')}</span>
        </div>

        {currentICL?.type === WorkType.icl && (
          <div className="m-auto w-fit pb-[20px]">
            <Switch
              checked={isCorrection}
              onClick={() => setIsCorrection(val => !val)}
              inputProps={{ 'aria-label': 'controlled' }}
            />
            <span className="title">{t('correction')}</span>
          </div>
        )}

        <div className="mb-[20px] w-full">
          <FormControl fullWidth>
            <InputLabel id="student-label">{isGroup ? t('select group') : t('wrapStudent')}</InputLabel>

            <Select
              labelId="student-label"
              sx={{ borderRadius: 25, width: '100%', background: 'white' }}
              id="student-hw-select"
              value={isGroup ? selectedGroupName : selectedStudentName}
              label={isGroup ? t('select group') : t('wrapStudent')}
              onChange={isGroup ? changeStudentGroup : changeStudent}
              MenuProps={{
                PaperProps: {
                  style: {
                    borderWidth: '2px',
                    borderColor: '#52C7FA',
                    marginTop: '6px',
                    borderRadius: '20px',
                  },
                },
              }}
            >
              {isGroup
                ? studentsGroups.length > 0 &&
                  studentsGroups
                    .sort((a, b) => a.groupName.localeCompare(b.groupName))
                    .map((item, i) => {
                      const englishSubject = (option && subjectsObj[option]) || option;
                      const subArr = Object.values(item.subjects);

                      if (!admins) {
                        const options = subArr.map((itemSub, index) => {
                          if (
                            englishSubject === itemSub.name &&
                            itemSub.attachedTeacher &&
                            itemSub.attachedTeacher.id === user.mail
                          ) {
                            return (
                              <MenuItem key={index} value={item.groupName}>
                                {item.groupName}
                              </MenuItem>
                            );
                          }
                        });
                        return options;
                      } else {
                        const options = subArr.map((itemSub, index) => {
                          if (englishSubject === itemSub.name && itemSub.attachedTeacher) {
                            return (
                              <MenuItem key={index} value={item.groupName}>
                                {item.groupName}
                              </MenuItem>
                            );
                          }
                        });
                        return options;
                      }
                    })
                : students?.length > 0 &&
                  students
                    ?.sort((a, b) => {
                      const nameA = a.FIO || a.Fio || '';
                      const nameB = b.FIO || b.Fio || '';
                      return nameA.localeCompare(nameB);
                    })
                    .map((item: any, i: number) => {
                      if (!option) return null;

                      const subjectInEnglish = subjectsObj?.[option];
                      if (!subjectInEnglish) return null;

                      const subject = item?.subjects?.[subjectInEnglish];
                      if (!subject) return null;

                      const hasActiveSub = subject && subject.activeSub;
                      const isAttachedTeacherIdMatch =
                        subject && subject.attachedTeacher && subject.attachedTeacher.id === user?.mail;
                      const adminMaterial = subject && subject.activeSub && subject.attachedTeacher;
                      if (!admins) {
                        if (hasActiveSub && isAttachedTeacherIdMatch) {
                          return (
                            <MenuItem key={i} value={item.FIO || item.Fio}>
                              {item.FIO || item.Fio}
                            </MenuItem>
                          );
                        }
                      } else {
                        if (adminMaterial) {
                          return (
                            <MenuItem key={i} value={item.FIO || item.Fio}>
                              {item.FIO || item.Fio}
                            </MenuItem>
                          );
                        }
                      }
                    })}
            </Select>
          </FormControl>
        </div>

        <div className="flex gap-[12px]">
          <button
            className="btn btn-primary more h-[43px] w-1/2 font-bold"
            onClick={sendICLToStudents}
            disabled={checkIfSubmitButtonDisabled()}
          >
            {t('send')}
          </button>

          <button className="btn more h-[43px] w-1/2 font-bold" onClick={closeModal}>
            {t('cancel')}
          </button>
        </div>
      </div>
    </Modal>
  );
};
